import { memo, useState, useRef, useEffect, useMemo } from 'react'
import Autocomplete from '@mui/material/Autocomplete'
import TextField from '@mui/material/TextField'
import { getFieldId } from '../utils/utils'
import { containsKeys } from '../utils/filterUtils'
import FieldHistory from '../../../components/DataEntry/FieldHistory.js'
import { FIELD_TYPES } from '../../../utility/constants.js'
import { useProcessContext } from '../../../../src/store.js'

const MultiSelectField = ({
  id,
  field,
  value,
  onChange,
  options = [],
  defaultValue = [],
  label = null,
  placeholder = null,
  className = '',
  disabled = false,
  helperText = null,
  autoFocus = false,
  getOptionLabel = (option) => option.value,
  listDataMetadata,
  allValues,
  inputSx = {},
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const inputRef = useRef(null)
  const [computedHelperText, setComputedHelperText] = useState(helperText)
  const processContext = useProcessContext((state) => state.processContext)


  const getMultiSelectValues = () => {
    return (
      options?.filter((ele) => {
        if (Array.isArray(value)) {
          return value.includes(ele.id)
        }
        return false
      }) || []
    )
  }

  useEffect(() => {
    if (field.data_structure === FIELD_TYPES.LIST_MULTI && Array.isArray(value)) {
      const selectedValues = value
        .map((id) => {
          const item =
            options?.find((entry) => entry.id === id)
          return item ? item.value || item.list_data_value : null
        })
        .filter(Boolean)
        .join('\n')

      setComputedHelperText(selectedValues || 'Please select an option')
    }
  }, [value, field, listDataMetadata, options])

  const zigzagFilterOptions = (options, { inputValue }) => {
    return options.filter((option) => containsKeys(option?.value, inputValue))
  }

  const handleDropdownOpen = () => {
    setIsDropdownOpen(true)
    setTimeout(() => {
      inputRef.current?.focus()
    }, 0)
  }

  const handleDropdownClose = () => {
    setIsDropdownOpen(false)
  }

  const autocompleteComponent = (
    <Autocomplete
      name={getFieldId(id)}
      multiple
      className={className}
      disabled={disabled}
      limitTags={2}
      options={options}
      value={getMultiSelectValues()}
      getOptionLabel={getOptionLabel}
      onChange={onChange}
      defaultValue={defaultValue}
      filterOptions={zigzagFilterOptions}
      open={isDropdownOpen}
      onOpen={handleDropdownOpen}
      onClose={handleDropdownClose}
      forcePopupIcon={true}
      disableClearable
      renderInput={(params) => (
        <TextField
          {...params}
          inputRef={inputRef}
          label={label}
          placeholder={placeholder}
          autoFocus={autoFocus}
          variant="outlined"
          color="primary"
          size="small"
          error={helperText != null}
          sx={inputSx}
        />
      )}
      fullWidth
      size="small"
      sx={inputSx}
    />
  )

  if (!field || field.is_subfield) {
    return autocompleteComponent
  } else {
    return (
      <div onClick={handleDropdownOpen}>
        {!isDropdownOpen ? (
          <FieldHistory
            fieldId={field?.id}
            field={field}
            listDataMetadata={listDataMetadata}
            dynamicListData={{
              ...processContext?.dynamicListData,
            }}
            processId={processContext?.processData?.id}
            allValues={allValues}
            helperText={
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '4px',
                  whiteSpace: 'pre-wrap',
                  lineHeight: 1.4,
                }}
              >
                {(helperText || computedHelperText)?.split('\n').map((line, index) => (
                  <div key={index}>{line}</div>
                ))}
              </div>
            }
          >
            {autocompleteComponent}
          </FieldHistory>
        ) : (
          autocompleteComponent
        )}
      </div>
    )
  }
}

export default memo(
  MultiSelectField,
  (prevProps, nextProps) =>
    prevProps.value === nextProps.value &&
    prevProps.options === nextProps.options &&
    prevProps.helperText === nextProps.helperText
)
